/**
 * RhJustificatifsRecus — bloc « Justificatifs reçus » (canal sécurisé arrêts).
 * Affiché dans l'onglet Absences côté RH. Valide / refuse les arrêts transmis.
 */
import { useState, useEffect } from 'react';
import { Card, Button, Badge, Alert } from '@/components/ui';
import { api } from '@/lib/api';
import { RhWho } from './rh-ui';
import type { RhJustificatif } from '@roronoa-collectivites/shared-types';

const TYPE_LABEL: Record<string, string> = { maladie: 'Maladie', accident_travail: 'Accident du travail', autre: 'Autre' };

export function RhJustificatifsRecus() {
  const [items, setItems] = useState<RhJustificatif[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function charger() {
    try { setItems(await api.rh.listJustificatifs('en_attente')); } catch { /* ignore */ }
    finally { setLoaded(true); }
  }
  useEffect(() => { void charger(); }, []);

  async function statuer(id: string, statut: 'valide' | 'refuse') {
    try {
      const r = await api.rh.statuerJustificatif(id, statut);
      if (statut === 'valide') setMsg(r.absenceId ? "Arrêt validé — l'absence a été ajoutée au calendrier de l'agent." : 'Arrêt validé.');
      else setMsg('Arrêt refusé.');
      await charger();
    } catch { /* ignore */ }
  }

  if (!loaded) return null;
  if (items.length === 0) {
    return msg ? <Alert variant="success">{msg}</Alert> : null;
  }

  return (
    <Card className="overflow-hidden p-0">
      {msg && <div className="border-b border-border px-4 py-2"><Alert variant="success">{msg}</Alert></div>}
      <div className="flex items-center gap-2.5 border-b border-border px-4 py-3">
        <Badge variant="rh">{items.length}</Badge>
        <h2 className="font-display text-sm font-semibold text-ink">Justificatifs d'arrêts reçus</h2>
        <span className="ml-auto text-xs text-ink-subtle">Canal sécurisé — visible RH uniquement</span>
      </div>
      <ul className="divide-y divide-border">
        {items.map((j) => (
          <li key={j.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
            <RhWho nom={j.agentNom} />
            <div className="min-w-0">
              <div className="text-sm text-ink"><Badge variant="neutral">{TYPE_LABEL[j.type] ?? j.type}</Badge> <span className="text-ink-muted">{new Date(`${j.dateDebut}T00:00:00`).toLocaleDateString('fr-FR')} → {new Date(`${j.dateFin}T00:00:00`).toLocaleDateString('fr-FR')}</span></div>
              {j.nomFichier ? <div className="text-xs text-ink-subtle">Pièce : {j.nomFichier}</div> : <div className="text-xs text-ink-subtle italic">Sans pièce (transmission déclarative)</div>}
            </div>
            <div className="ml-auto flex items-center gap-1.5">
              <Button variant="secondary" size="sm" onClick={() => void statuer(j.id, 'valide')}>Valider</Button>
              <button type="button" onClick={() => void statuer(j.id, 'refuse')} className="text-xs text-ink-subtle hover:text-danger">Refuser</button>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
